import React, { useEffect, useState, useMemo } from "react";
import { Box, Modal, Typography, IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import { getPDFLink } from "../../../util/pdf-functions";
import { PDFViewer as ReactPDFViewer, pdf } from "@react-pdf/renderer";
import { ExpenditureReport } from "../../../pages/pos/customer/ExpenditureReport";
import FinancialMonthlyReport from "../../../pages/pos/customer/FinancialMonthlyReport";
import FinancialAnnualReport from "../../../pages/pos/customer/FinancialAnnualReport";
import FinancialCustomReport from "../../../pages/pos/customer/FinancialCustomReport";
import OutstandingReport from "../../../pages/pos/customer/OutstandingReport";
import DeptWisePieceCountMonthlyReport from "../../../pages/pos/customer/DeptWisePieceCountMonthlyReport";
import DeptWisePieceCountAnnualReport from "../../../pages/pos/customer/DeptWisePieceCountAnnualReport";
import DeptWisePieceCountCustomReport from "../../../pages/pos/customer/DeptWisePieceCountCustomReport";
import PDFDownloadAccessModal from "../pdf-download-access-modal/PDFDownloadAccessModal";
import PDFPrintAccessModal from "../pdf-print-modal/Pdf-print-modal";
import { ICustomerReportData } from "../../../typings/interfaces/responses/customer-report-data-response";

interface PdfViewModalProps {
  open: boolean;
  onClose: () => void;
  onOpenAccessModal: () => void;
  onOpenPrintAccessModal: () => void;
  reportName: string | null;
  name: string;
  customerId: string;
  address: string;
  contactNumber: string;
  total: number;
  month: string;
  reportData?: ICustomerReportData | null;
  notes?: string;
}

const PdfViewModal: React.FC<PdfViewModalProps> = ({
  open,
  onClose,
  onOpenAccessModal,
  onOpenPrintAccessModal,
  reportName: report,
  name,
  customerId,
  address,
  contactNumber,
  total,
  month,
  reportData,
  notes,
}) => {
  const [PDFLink, setPDFLink] = React.useState<string | null>(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);

  useEffect(() => {
    getPDFLink().then((link) => {
      setPDFLink(link);
    });
  }, []);

  const customerName = reportData?.customerName || name;
  const customerRef = reportData?.customerRef || customerId;
  const customerAddress = reportData?.address || address;
  const customerContact = reportData?.contactNumber || contactNumber;
  const reportTitle = reportData?.reportTitle || report || "Report";
  const period = reportData?.period || month;
  const reportType = reportData?.reportType ?? report ?? "";
  const financialRows = reportData?.financialRows ?? [];
  const pieceCountRows = reportData?.pieceCountRows ?? [];
  const outstandingRows = reportData?.outstandingRows ?? [];
  const lastPaymentAmount = reportData?.lastPaymentAmount ?? 0;
  const lastPaymentDate = reportData?.lastPaymentDate ?? "—";
  const totalDueAmount = reportData?.totalDueAmount ?? reportData?.totalAmount ?? 0;

  const isExpenditure = /expenditure/i.test(reportType);
  const isOutstanding = /outstanding/i.test(reportType);
  const isPieceCount = /invoice/i.test(reportType);
  const isAnnual = /annual/i.test(reportTitle);
  const isCustom = /custom/i.test(reportTitle);

  const sharedProps = {
    customerName,
    customerRef,
    address: customerAddress,
    contactNumber: customerContact,
    reportTitle,
    period,
    notes,
  };

  const buildDocument = () => {
    if (isExpenditure) {
      return (
        <ExpenditureReport
          name={customerName}
          customerId={customerRef}
          address={customerAddress}
          contactNumber={customerContact}
          total={reportData?.totalAmount ?? total}
          month={period}
          reportTitle={reportTitle ?? undefined}
          notes={notes}
        />
      );
    }
    if (isOutstanding) {
      return (
        <OutstandingReport
          name={customerName}
          customerId={customerRef}
          address={customerAddress}
          contactNumber={customerContact}
          lastPaymentAmount={lastPaymentAmount}
          lastPaymentDate={lastPaymentDate}
          totalDueAmount={totalDueAmount}
          outstandingRows={outstandingRows}
          reportTitle={reportTitle ?? undefined}
          period={period}
          notes={notes}
        />
      );
    }
    if (isPieceCount) {
      if (isAnnual) return <DeptWisePieceCountAnnualReport rows={pieceCountRows} {...sharedProps} />;
      if (isCustom) return <DeptWisePieceCountCustomReport rows={pieceCountRows} {...sharedProps} />;
      return <DeptWisePieceCountMonthlyReport rows={pieceCountRows} {...sharedProps} />;
    }
    return (
      <ExpenditureReport
        name={customerName}
        customerId={customerRef}
        address={customerAddress}
        contactNumber={customerContact}
        total={reportData?.totalAmount ?? total}
        month={period}
        reportTitle={report ?? undefined}
        notes={notes}
      />
    );
  };

  const memoizedDocument = useMemo(
    () => buildDocument(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportData, name, customerId, address, contactNumber, total, month, report, notes]
  );

  const handleDownloadClick = () => setDownloadModalOpen(true);
  const handleDownloadModalClose = () => setDownloadModalOpen(false);

  const handleAccessGranted = async () => {
    try {
      const blob = await pdf(buildDocument()).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reportTitle}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloadModalOpen(false);
    }
  };

  const handlePrintClick = () => setPrintModalOpen(true);
  const handlePrintModalClose = () => setPrintModalOpen(false);

  const handlePrintAccessGranted = async () => {
    try {
      setPrintModalOpen(false);
      const blob = await pdf(buildDocument()).toBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow?.print();
        URL.revokeObjectURL(url);
      };
    } catch (error) {
      console.error("Print failed:", error);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        disableScrollLock
        disableRestoreFocus
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(0, 0, 0, 0.07)",
            backdropFilter: "blur(0.04rem)",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: pxToRem(1344),
            bgcolor: Colors.WHITE,
            p: 4,
            borderRadius: "0.71rem",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              color: Colors.MAIN_GRAY,
            }}
          >
            <CloseIcon sx={{ fontSize: "1.571rem" }} />
          </IconButton>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Box display="flex" alignItems="center">
              <Typography sx={{ color: Colors.PRIMARY, fontSize: "1.85714285714rem" }}>
                {report || "Report"}
              </Typography>
              {!isCustom && (
                <Typography
                  sx={{
                    marginLeft: "7rem",
                    color: Colors.PRIMARY,
                    fontSize: "1.42857142857rem",
                  }}
                >
                  {period}
                </Typography>
              )}
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box
                sx={{
                  backgroundColor: Colors.WHITE,
                  border: `1px solid ${Colors.PRIMARY}`,
                  borderRadius: "0.5rem",
                  width: "4.28571428571rem",
                  height: "3rem",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: "1.5rem",
                }}
              >
                <IconButton onClick={handleDownloadClick}>
                  <DownloadIcon sx={{ fontSize: "2rem", color: Colors.PRIMARY }} />
                </IconButton>
              </Box>
              <Box
                sx={{
                  backgroundColor: Colors.WHITE,
                  border: `1px solid ${Colors.PRIMARY}`,
                  borderRadius: "0.5rem",
                  width: "4.28571428571rem",
                  height: "3rem",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginRight: "1.5rem",
                }}
              >
                <IconButton onClick={handlePrintClick}>
                  <PrintIcon sx={{ fontSize: "2rem", color: Colors.PRIMARY }} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              marginTop: "1rem",
              width: "100%",
              height: "53.6428571429rem",
              bgcolor: Colors.BACKSHADE_GRAY,
              borderRadius: "0.5rem",
              overflow: "hidden",
            }}
          >
            <ReactPDFViewer
              key={reportData ? `${reportData.reportType}-${reportData.period}` : report ?? "loading"}
              width="100%"
              height="100%"
              showToolbar={false}
            >
              {memoizedDocument}
            </ReactPDFViewer>
          </Box>
        </Box>
      </Modal>

      <PDFDownloadAccessModal
        open={downloadModalOpen}
        onClose={handleDownloadModalClose}
        onAccessGranted={handleAccessGranted}
      />

      <PDFPrintAccessModal
        open={printModalOpen}
        onClose={handlePrintModalClose}
        onPrintAccessGranted={handlePrintAccessGranted}
      />
    </>
  );
};

export default PdfViewModal;
